import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, CalendarIcon } from 'lucide-react';
import { useOrderSizes } from '@/hooks/useOrderSizes';
import { orderSizeOptions } from '@/utils/orderSizeOptions';
import SizeChoicesUnavailable from '@/components/SizeChoicesUnavailable';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { parseDeliveryDate, formatDateForBackend, formatDeliveryDateDisplay } from '../utils/dateUtils';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import AddressConfirmationDialog from '@/components/AddressConfirmationDialog';
import useManualOrder from '@/hooks/useManualOrder';

const EMPTY_FORM = {
  recipient_name: '',
  recipient_street_address: '',
  recipient_street_address_2: '',
  recipient_city: '',
  recipient_state: '',
  recipient_zip_code: '',
  recipient_phone: '',
  // Sender (bill-to) — our customer on a direct order. All optional.
  sender_name: '',
  sender_street_address: '',
  sender_street_address_2: '',
  sender_city: '',
  sender_state: '',
  sender_zip_code: '',
  sender_phone: '',
  order_number: '',
  network_source: '',
  delivery_date: '',
  order_accepted_date: '',
  card_message: '',
  special_instructions: '',
  product_description: '',
  size: '',
  delivery_time_requirement: '',
  total_price: ''
};

// Manual orders record their source as a raw channel label in network_source,
// mapped to a curated Source in Settings like any other source string (Slice 2).
const MANUAL_CHANNELS = ['Phone', 'Walk-In', 'Scenic Wedding'];

// A fresh form with the Accepted Date defaulted to today (the user can override it).
const makeEmptyForm = () => ({ ...EMPTY_FORM, order_accepted_date: formatDateForBackend(new Date()) });

// Titled sub-panel header with an optional ship-to / bill-to pill.
const PanelHeader = ({ title, tag, tagClass = '' }) => (
  <div className="flex items-center gap-2">
    <h3 className="text-sm font-semibold text-stone-900">{title}</h3>
    {tag && (
      <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${tagClass}`}>
        {tag}
      </span>
    )}
  </div>
);

export const ManualOrderForm = ({ onOrderCreated }) => {
  const manual = useManualOrder(makeEmptyForm, onOrderCreated);
  const { fields: formData, edit: setFormData, pending: loading, submit: handleSubmit } = manual;
  const addressConfirmation = manual.record?.difference;
  const activeSizes = useOrderSizes();
  const sizes = orderSizeOptions(activeSizes, formData.size);

  return (
    <>
    {addressConfirmation && (
      <AddressConfirmationDialog
        difference={addressConfirmation}
        proposal={manual.record?.addressReview?.proposal}
        reason={manual.record?.addressReview?.reason}
        busy={loading}
        onAccept={() => handleSubmit({ acceptCanonicalAddress: true })}
        onEdit={manual.dismissAddress}
      />
    )}
    <Card data-testid="manual-entry-card" className="border-stone-300">
      <CardHeader>
        <CardTitle>Manual Order Entry</CardTitle>
        <CardDescription>Enter order details manually when you don't have a document to upload</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">

        <p className="text-sm text-stone-600">Unfinished submissions are retained only in this open app tab. Reloading or closing it loses this recovery information. Orders already saved for desktop Review remain saved.</p>
        {manual.record?.message && <p role="status" data-testid="manual-outcome">{manual.record.message}</p>}
        {!manual.record && <p role="status">Sign in to a current account to enter an order.</p>}
        {manual.record?.status === 'uncertain' && (
          <Button onClick={() => handleSubmit()} data-testid="manual-retry-button">Retry same submission</Button>
        )}
        {manual.record?.receipt && (
          <Button variant="outline" onClick={manual.publish} data-testid="manual-show-saved">Show saved order in recent list</Button>
        )}
        <fieldset disabled={manual.locked} className="space-y-5" data-testid="manual-fields">

        {/* Row 1 — Recipient + Sender */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Recipient */}
          <div className="rounded-xl border border-stone-200 p-4 space-y-3">
            <PanelHeader title="Recipient" tag="Ship to" tagClass="bg-emerald-50 text-emerald-700" />

            <div className="grid grid-cols-[2fr_1fr] gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="manual-recipient-name">Recipient Name <span className="text-red-500">*</span></Label>
                <Input
                  id="manual-recipient-name"
                  value={formData.recipient_name}
                  onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                  placeholder="Enter recipient name"
                  data-testid="manual-input-recipient-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-phone">Phone</Label>
                <Input
                  id="manual-phone"
                  value={formData.recipient_phone}
                  onChange={(e) => setFormData({ ...formData, recipient_phone: e.target.value })}
                  placeholder="(702) 555-0123"
                  data-testid="manual-input-phone"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="manual-street-address">Street Address <span className="text-red-500">*</span></Label>
              <AddressAutocomplete
                disabled={manual.locked}
                value={formData.recipient_street_address}
                onChange={(value) => setFormData(prev => ({ ...prev, recipient_street_address: value }))}
                onAddressSelect={(address) => {
                  setFormData(prev => ({
                    ...prev,
                    recipient_street_address: address.street,
                    recipient_city: address.city || prev.recipient_city,
                    recipient_state: address.state || prev.recipient_state,
                    recipient_zip_code: address.zip || prev.recipient_zip_code
                  }));
                }}
                placeholder="Start typing an address..."
              />
            </div>

            <div className="grid grid-cols-[1.5fr_0.7fr_0.4fr_0.7fr] gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="manual-street-address-2">Address 2 (apt., biz name, etc.)</Label>
                <Input
                  id="manual-street-address-2"
                  value={formData.recipient_street_address_2}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipient_street_address_2: e.target.value }))}
                  placeholder="Apt 4B, Suite 100, etc."
                  data-testid="manual-input-street-address-2"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-city">City</Label>
                <Input
                  id="manual-city"
                  value={formData.recipient_city}
                  onChange={(e) => setFormData({ ...formData, recipient_city: e.target.value })}
                  placeholder="City"
                  data-testid="manual-input-city"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-state">State</Label>
                <Input
                  id="manual-state"
                  value={formData.recipient_state}
                  onChange={(e) => setFormData({ ...formData, recipient_state: e.target.value })}
                  placeholder="NV"
                  maxLength={2}
                  data-testid="manual-input-state"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-zip">Zip Code <span className="text-red-500">*</span></Label>
                <Input
                  id="manual-zip"
                  value={formData.recipient_zip_code}
                  onChange={(e) => setFormData({ ...formData, recipient_zip_code: e.target.value })}
                  placeholder="89101"
                  data-testid="manual-input-zip"
                />
              </div>
            </div>
          </div>

          {/* Sender */}
          <div className="rounded-xl border border-stone-200 p-4 space-y-3">
            <PanelHeader title="Sender" tag="Bill to" tagClass="bg-blue-50 text-blue-700" />

            <div className="grid grid-cols-[2fr_1fr] gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="manual-sender-name">Sender Name</Label>
                <Input
                  id="manual-sender-name"
                  value={formData.sender_name}
                  onChange={(e) => setFormData({ ...formData, sender_name: e.target.value })}
                  placeholder="Enter sender name"
                  data-testid="manual-input-sender-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-sender-phone">Phone</Label>
                <Input
                  id="manual-sender-phone"
                  value={formData.sender_phone}
                  onChange={(e) => setFormData({ ...formData, sender_phone: e.target.value })}
                  placeholder="(702) 555-0123"
                  data-testid="manual-input-sender-phone"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="manual-sender-street-address">Billing Address</Label>
              <AddressAutocomplete
                disabled={manual.locked}
                value={formData.sender_street_address}
                onChange={(value) => setFormData(prev => ({ ...prev, sender_street_address: value }))}
                onAddressSelect={(address) => {
                  setFormData(prev => ({
                    ...prev,
                    sender_street_address: address.street,
                    sender_city: address.city || prev.sender_city,
                    sender_state: address.state || prev.sender_state,
                    sender_zip_code: address.zip || prev.sender_zip_code
                  }));
                }}
                placeholder="Start typing an address..."
              />
            </div>

            <div className="grid grid-cols-[1.5fr_0.7fr_0.4fr_0.7fr] gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="manual-sender-street-address-2">Address 2 (apt., biz name, etc.)</Label>
                <Input
                  id="manual-sender-street-address-2"
                  value={formData.sender_street_address_2}
                  onChange={(e) => setFormData(prev => ({ ...prev, sender_street_address_2: e.target.value }))}
                  placeholder="Apt 4B, Suite 100, etc."
                  data-testid="manual-input-sender-street-address-2"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-sender-city">City</Label>
                <Input
                  id="manual-sender-city"
                  value={formData.sender_city}
                  onChange={(e) => setFormData({ ...formData, sender_city: e.target.value })}
                  placeholder="City"
                  data-testid="manual-input-sender-city"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-sender-state">State</Label>
                <Input
                  id="manual-sender-state"
                  value={formData.sender_state}
                  onChange={(e) => setFormData({ ...formData, sender_state: e.target.value })}
                  placeholder="NV"
                  maxLength={2}
                  data-testid="manual-input-sender-state"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-sender-zip">Zip Code</Label>
                <Input
                  id="manual-sender-zip"
                  value={formData.sender_zip_code}
                  onChange={(e) => setFormData({ ...formData, sender_zip_code: e.target.value })}
                  placeholder="89101"
                  data-testid="manual-input-sender-zip"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Row 2 — Product & Pricing + Delivery & Order */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Product & Pricing */}
          <div className="rounded-xl border border-stone-200 p-4 space-y-3">
            <PanelHeader title="Product & Pricing" />

            <div className="space-y-1.5">
              <Label htmlFor="manual-product-description">Product Description <span className="text-red-500">*</span></Label>
              <Textarea
                id="manual-product-description"
                value={formData.product_description}
                onChange={(e) => setFormData({ ...formData, product_description: e.target.value })}
                placeholder="e.g., Dozen red roses in clear vase, Mixed spring arrangement, Funeral standing spray..."
                rows={3}
                data-testid="manual-input-product-description"
                className="resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="manual-size">Size</Label>
                <Select
                  disabled={!activeSizes.length}
                  value={formData.size || '__none__'}
                  onValueChange={(value) => setFormData({ ...formData, size: value === '__none__' ? '' : value })}
                >
                  <SelectTrigger id="manual-size" data-testid="manual-input-size">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {sizes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!activeSizes.length && <SizeChoicesUnavailable />}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-total-price">Total Price <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500">$</span>
                  <Input
                    id="manual-total-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.total_price}
                    onChange={(e) => setFormData({ ...formData, total_price: e.target.value })}
                    placeholder="0.00"
                    className="pl-7"
                    data-testid="manual-input-total-price"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Delivery & Order */}
          <div className="rounded-xl border border-stone-200 p-4 space-y-3">
            <PanelHeader title="Delivery & Order" />

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="manual-delivery-date">Date <span className="text-red-500">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="manual-input-delivery-date">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.delivery_date ? formatDeliveryDateDisplay(formData.delivery_date) : null}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={parseDeliveryDate(formData.delivery_date)}
                      onSelect={(date) => setFormData({ ...formData, delivery_date: date ? formatDateForBackend(date) : '' })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-delivery-time">Time Requirement</Label>
                <Input
                  id="manual-delivery-time"
                  value={formData.delivery_time_requirement}
                  onChange={(e) => setFormData({ ...formData, delivery_time_requirement: e.target.value })}
                  placeholder='e.g., "By 12:00 PM"'
                  data-testid="manual-input-delivery-time-requirement"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-accepted-date">Accepted Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="manual-input-accepted-date">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.order_accepted_date ? formatDeliveryDateDisplay(formData.order_accepted_date) : null}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={parseDeliveryDate(formData.order_accepted_date)}
                      onSelect={(date) => setFormData({ ...formData, order_accepted_date: date ? formatDateForBackend(date) : '' })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="manual-order-number">Order Number</Label>
                <Input
                  id="manual-order-number"
                  value={formData.order_number}
                  onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                  placeholder="Network order #"
                  data-testid="manual-input-order-number"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-source">Source</Label>
                <Select
                  value={formData.network_source}
                  onValueChange={(value) => setFormData({ ...formData, network_source: value })}
                >
                  <SelectTrigger data-testid="manual-input-source">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {MANUAL_CHANNELS.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Row 3 — Notes */}
        <div className="rounded-xl border border-stone-200 p-4 space-y-3">
          <PanelHeader title="Notes" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="manual-card-message">Card Message</Label>
              <Textarea
                id="manual-card-message"
                value={formData.card_message}
                onChange={(e) => setFormData({ ...formData, card_message: e.target.value })}
                placeholder="Personal message for the recipient's card"
                rows={3}
                data-testid="manual-input-card-message"
                className="resize-none"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-special-instructions">Special Instructions</Label>
              <Textarea
                id="manual-special-instructions"
                value={formData.special_instructions}
                onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                placeholder="Gate codes, delivery timing, where to leave order, etc."
                rows={3}
                data-testid="manual-input-special-instructions"
                className="resize-none"
              />
            </div>
          </div>
        </div>

        </fieldset>
        {/* Action Buttons */}
        <div className="flex gap-3 pt-1">
          <Button
            onClick={() => handleSubmit()}
            disabled={manual.locked || manual.record?.status === 'accepted'}
            className="flex-1"
            data-testid="manual-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Order...
              </>
            ) : (
              'Create Order'
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => setFormData(makeEmptyForm())}
            disabled={manual.locked}
            data-testid="manual-reset-button"
          >
            Clear Form
          </Button>
        </div>
      </CardContent>
    </Card>
    </>
  );
};

export default ManualOrderForm;
